import { CanvasPcodeVideoFrameParams } from '../../service/Pcode/PcodeCanvasVideo';
import { ValidationErrorData } from '../../typings/common';
import {
    ApiGalleryItem,
    ApiItemStatus,
    CanvasApiBaseResponse,
    CanvasApiItemsResponse,
    StillageFileInfo
} from '../common/requestsTypings';
import { Order, ResourceType } from '../common/typings';

export enum GalleryPaneTypes {
    stock = 'stock',
    recent = 'recent',
    idea = 'idea'
}

export interface GalleriesState {
    isPopupOpened: boolean;
    error?: ValidationErrorData;

    currentResourceType: ResourceType;
    resources: GalleriesResources;

    imageStockCategoriesData?: GalleryCategoryData[];
    videoStockCategoriesData?: GalleryCategoryData[];
}

export interface GalleriesResources {
    [ResourceType.image]: GalleryResourceData<GalleryImageCollectionItem>;
    [ResourceType.audio]: GalleryResourceData<GalleryAudioCollectionItem>;
    [ResourceType.video]: GalleryResourceData<GalleryVideoCollectionItem>;
}

export interface GalleryCommonPaneState<T> {
    isAllItemsLoading: boolean;
    isMoreItemsLoading: boolean;
    isFetched: boolean;

    data: GalleryDataForPane<T>;
}

export interface GalleryStockPaneData<T> extends GalleryCommonPaneState<T> {
    filters: GalleryStockPaneFilters;
}

export interface GalleryRecentPaneData<T> extends GalleryCommonPaneState<T> {
    filters: GalleryRecentPaneFilters;
}

export interface GalleryIdeasPaneData<T> extends GalleryCommonPaneState<T> {
    filters: GalleryIdeasPaneFilters;
}

export type GalleryPaneData<T> = GalleryStockPaneData<T> | GalleryRecentPaneData<T> | GalleryIdeasPaneData<T>;

export type GalleryPanesRecord<T> = {
    [GalleryPaneTypes.stock]: GalleryStockPaneData<T>;
    [GalleryPaneTypes.recent]: GalleryRecentPaneData<T>;
    [GalleryPaneTypes.idea]: GalleryIdeasPaneData<T>;
};

export interface GalleryResourceData<T> {
    fileEditorId?: string;

    currentPaneType: GalleryPaneTypes;
    enabledGalleryTypes: GalleryPaneTypes[];
    allGalleryTypes: GalleryPaneTypes[];
    hoveredItemIndex?: number;
    preview?: PreviewParams;
    isBusy: boolean;

    panes: GalleryPanesRecord<T>;
}

export type GalleryCollectionItem =
    | GalleryImageCollectionItem
    | GalleryVideoCollectionItem
    | GalleryAudioCollectionItem;

export type GalleryDataForPane<T> = {
    collection: T[];
    total: number;
};

export interface GalleryCommonCollectionItem {
    type?: ResourceType;
    id: string;
    removable: boolean;

    date: string;
    archive: boolean;

    url: string;
    originalName?: string;
    stock_file_id?: string;
    sub_categories?: number[];
    stillage_id?: number;
    conversion_task_id?: number;
}

export interface GalleryBaseImageCollectionItem extends GalleryCommonCollectionItem {
    thumbnailUrl: string;
    previewUrl: string;
    largePreviewUrl: string;
    name?: string;
    width?: number;
    height?: number;
}

export interface GalleryCommonImageCollectionItem extends GalleryBaseImageCollectionItem {
    type: undefined;

    categories: number[];

    cropSourceUrl: string;
    stillageFileId?: string;
    stillageFileInfo: StillageFileInfo;
    originalFileId?: string;
    stockFileId?: string;
}

export interface GalleryPackshotCollectionItem extends GalleryBaseImageCollectionItem {
    type: ResourceType.image;

    formats: Array<{
        url: string;
        height: number;
        width: number;
        path: string;
        size: string;
    }>;

    status: string;
    stillage_id?: number;
}

export type GalleryImageCollectionItem = GalleryCommonImageCollectionItem | GalleryPackshotCollectionItem;

export interface GalleryVideoCollectionItem extends GalleryCommonCollectionItem {
    type: ResourceType.video;
    thumbnailUrl: string;
    name?: string;

    thumbnail: {
        url: string;
        width: number;
        height: number;
        preview: {
            url: string;
            width: number;
            height: number;
        };
    };
    overlayColor: string;
    formats: Array<{
        url: string;
        type?: string;
        delivery?: string;
        bitrate?: number;
    }>;

    status: ApiItemStatus;
}

export interface GalleryAudioCollectionItem extends GalleryCommonCollectionItem {
    type: ResourceType.audio;
    name: string;
    duration: number;
    durationLabel: string;

    sub_categories?: number[];
    stock_file_id?: string;
}

export interface GalleryCommonPaneFilters {
    offset: number;
    limit: number;
}

export interface GalleryStockPaneFilters extends GalleryCommonPaneFilters {
    selectedGalleryCategories: string[];
    semanticText: string;
}

export interface GalleryRecentPaneFilters extends GalleryCommonPaneFilters {
    recentNameFilter: string;
    recentOrder: Order;
}

export interface GalleryIdeasPaneFilters extends GalleryCommonPaneFilters {}

export type GalleryPaneFilters = GalleryIdeasPaneFilters | GalleryStockPaneFilters | GalleryRecentPaneFilters;

export type EmptyGalleryPanesRecord = Partial<Record<GalleryPaneTypes, boolean>>;

export interface PreviewParams {
    paneType: GalleryPaneTypes;
    itemIndex: number;
    itemSource?: Partial<CanvasPcodeVideoFrameParams> | string;
    itemName?: string;
    hasPrev?: boolean;
    hasNext?: boolean;
    error?: ValidationErrorData;
}

export interface GalleryCategoryData {
    id: string;
    name: string;
}

export interface FetchGalleryCategoriesResult {
    galleryType: GalleryPaneTypes;
    rawData: GalleryCategoryData[];
}

export interface CommonGalleryProps {
    currentPaneType: GalleryPaneTypes;
    isPopupOpened: boolean;
    enabledGalleryTypes: GalleryPaneTypes[];
    emptyGalleriesMap: EmptyGalleryPanesRecord;
    preview?: PreviewParams;

    removeItem: (collectionIndex: number, itemName?: string) => void;
    selectTab: (currentPaneType: GalleryPaneTypes) => void;
    closeGalleryPopup: () => void;
    applyItem: (collectionIndex?: number) => void;
    editItemName: (collectionIndex: number, name: string) => void;
    isBusy: boolean;
    error?: ValidationErrorData;
    fetchInitialData: (resourceType: ResourceType) => void;
}

export interface PreviewImageItemParams {
    itemIndex: number;
    hasPrev: boolean;
    hasNext: boolean;
    itemSource: string;
    itemName?: string;
}

export interface PreviewVideoItemParams {
    itemIndex: number;
    hasPrev: boolean;
    hasNext: boolean;
}

export interface PreviewAudioItemParams {
    itemIndex: number;
    itemSource: string;
}

export interface GalleriesRequestData extends GalleryCommonPaneFilters {
    name: string;
    semantic_search: string;
    sort_order: Order;
    categories?: string[];
    groups?: string[];
}

export type FetchGalleryItemsResponse = CanvasApiItemsResponse<ApiGalleryItem>;
export type FetchGalleryCategoriesResponse = CanvasApiItemsResponse<GalleryCategoryData>;
export type GalleryItemResponse = CanvasApiBaseResponse & ApiGalleryItem;
